import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { DateRangePicker } from "react-date-range";
import moment from "moment";
import DataTable from "react-data-table-component";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { FaCalendar } from "react-icons/fa6";
import { IoEyeSharp } from "react-icons/io5";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* FORM CONTROL */
import { useFormik } from "formik";
import * as yup from "yup";

/* IMPORTED APIs */
import { deleteRegistration, getRegistrationList } from "../../../service/api/auth.api";

/* DATA */
import vendors from "../../../data/vendors";

/* USER DEFINED COMPONENTS */
import InputField from "../../../components/input";
import { FiTrash } from "react-icons/fi";
import { Link } from "react-router-dom";
import { BiEdit } from "react-icons/bi";
import { handleAddToken } from "../../../service/request";
import { useSelector } from "react-redux";

/* CLICK OUTSIDE HANDLER */
const useClickOutside = (handler) => {
  let domNode = useRef();

  useEffect(() => {
    let maybeHandler = (event) => {
      if (!domNode.current?.contains(event.target)) {
        handler();
      }
    };

    document.addEventListener("mousedown", maybeHandler);
    return () => {
      document.removeEventListener("mousedown", maybeHandler);
    };
  });

  return domNode;
};

const Vendors = () => {
   // GLOBAL VARIALBES
   const userDetails = useSelector((state) => state?.user);
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [tagList, setTagList] = useState([]);
  const [totalDocs, setTotalDocs] = useState([]);
  const [totalPages, setTotalPages] = useState(0);

  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  /* DATEPICKER VARIABES */
  const [datepicker, setDatepicker] = useState(false);
  const [startDate, setStartDate] = useState(
    moment(new Date().setMonth(new Date().getMonth() - 1)).format("DD/MM/YYYY")
  );
  const [endDate, setEndDate] = useState(
    moment(new Date()).format("DD/MM/YYYY")
  );

  let domNode = useClickOutside(() => {
    setDatepicker(false);
  });

  // Functions
  const registrationsGetFunction = async () => {
    const obj = {
      page,
      sizePerPage,
      pagination: true,
    };
    if (search) {
      obj.search = search;
    }
    if (selectedStatus) {
      obj.status = selectedStatus?.value;
    }
    await handleAddToken(userDetails?.token);
    setLoading(true);
    getRegistrationList(obj)
      .then((response) => {
        if (response?.status) {
          console.log("response", response);
          setTagList(response?.data?.docs);
          setTotalDocs(response?.data?.totalDocs);
          setTotalPages(response?.data?.totalPages);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleDeleteFunction = (vendorId) => {
    deleteRegistration(vendorId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((err) => {
        console.log("Err", err);
      })
      .finally(() => {
        registrationsGetFunction();
      });
  };
  const selectionRange = {
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No.",
      width: "10vh",
      selector: (row, index) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          #{row?.vendorId}
        </span>
      ),
    },
    {
      name: "Vendor Name",
      width: "30vh",
      selector: (row) => (
        <span className="text-sm font-proxima-bold text-black">
          {row?.firstName} {row?.lastName}
        </span>
      ),
    },
    {
      name: "	Vendor Email",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row.email}
        </span>
      ),
    },
    {
      name: "Vendor Phone	",
      width: "25vh",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.contactInformation?.phoneNumber}
        </span>
      ),
    },
    {
      name: "Status",
      selector: (row) =>
        row.status === "notApproved" ? (
          <span className="text-sm font-proxima-semibold text-red-500">
            Not Approved
          </span>
        ) : row.status === "approved" ? (
          <span className="text-sm font-proxima-semibold text-green-500">
            Approved
          </span>
        ) : (
          <span className="text-sm font-proxima-semibold text-yellow-800">
            Pending
          </span>
        ),
    },
    {
      name: "Time",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {moment(row?.createdAt).format("DD/MM/YYYY hh:mm a")}
        </span>
      ),
    },

    {
      name: "Actions",
      selector: (row) => (
        <div className="flex items-center">
          <Tooltip content="View" className="text-sm font-proxima-semibold">
            <Link to={"/vendor/view/" + row?._id} target="_blank">
              <Button className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5">
                <IoEyeSharp className="w-5 h-5 text-greyText" />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip content="Edit" className="text-sm font-proxima-semibold">
            <Link to={"/vendor/edit/" + row?._id} target="_blank">
              <Button className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5">
                <BiEdit className="w-5 h-5 text-greyText" />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip content="Delete" className="text-sm font-proxima-semibold">
            <Button
              onClick={() => {
                handleDeleteFunction(row?._id);
              }}
              className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
            >
              <FiTrash className="w-5 h-5 text-greyText" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setLoading(true);
    setTagList([]);
    setPage(selectedPage);
  };

  /* USE-EFFECTS */
  useEffect(() => {
    registrationsGetFunction();
  }, [page, sizePerPage, selectedStatus, search]);

  return (
    <section className="px-0 py-2 xl:p-10 lg:p-10 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Vendors | CIDA Perfumes</title>
      </Helmet>

      <div className="container-full-width">
        <div className="block xl:flex lg:flex items-center justify-between">
          {/* HEADING */}
          <div>
            <h1 className="text-3xl font-proxima-bold text-black tracking-tight">
              Vendor Registration List
            </h1>
            <h2 className="text-base xl:text-lg lg:text-lg font-proxima-regular text-greyText tracking-tight">
              Handle inquiries regarding the vendor registration list.
            </h2>
          </div>

          {/* SEARCH AND FILTERS */}
          <div className="block xl:flex lg:flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            {/* SEARCHBAR */}
            <div className="w-full xl:w-searchBar lg:w-searchBar">
              <InputField
                type="text"
                placeholder="Search..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => {
                  setSearch(e?.target?.value);
                }}
              />
            </div>

            {/* RANGEPICKER */}
            <div className="relative mt-2 xl:mt-0 lg:mt-0 w-full xl:w-auto lg:w-auto">
              <div
                ref={domNode}
                className="w-full xl:w-rangePicker lg:w-rangePicker flex items-center gap-3 border border-authBorder rounded-lg py-3 px-5 bg-white cursor-pointer"
              >
                <FaCalendar className="w-4 h-4 text-greyText" />
                <input
                  type="text"
                  name="dateRange"
                  placeholder="Select the date range"
                  value={`${startDate} - ${endDate}`}
                  onClick={() => setDatepicker(!datepicker)}
                  className="w-full bg-white outline-none font-proxima-semibold text-sm focus:outline-none active:outline-none placeholder:font-proxima-regular transition-all duration-300 ease-linear text-black"
                />
              </div>
              {datepicker && (
                <div className="absolute top-12 right-0 transition-all duration-300 ease-linear z-50">
                  <DateRangePicker
                    ranges={[selectionRange]}
                    dragSelectionEnabled
                    showPreview
                    onChange={(e) => {
                      setStartDate(
                        moment(e.selection.startDate).format("DD/MM/YYYY")
                      );
                      setEndDate(
                        moment(e.selection.endDate).format("DD/MM/YYYY")
                      );
                    }}
                    className="shadow-xl font-proxima-regular rounded-xl border border-authBorder cursor-pointer"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ORDERS TABLE */}
        <div className="mt-5">
          <DataTable
            className="data-table"
            data={tagList}
            columns={columns}
            responsive
            pagination
            paginationTotalRows={15}
            paginationServer
            noDataComponent={
              <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
                <p className="text-sm font-proxima-semibold text-black text-center">
                  There are no details to display.
                </p>
              </div>
            }
          />
        </div>
      </div>
    </section>
  );
};

export default Vendors;
